import { useEffect, useMemo, useState } from 'react';
import { Col, Row, Spin, Table, Typography, Result } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import API from 'src/services/api-service';

type WorkItemDetail = {
  itemName: string;
  itemCount: number | null;
  itemType: string;
};

type WorkloadRow = {
  orderId: number | string;
  orderLink?: string;
  saleName?: string;
  productCode: string;
  quantity: number | null;
  codAmount: number | null;
  tipAmount: number | null;
  installLabor: number | null;
  warrantyLabor: number | null;
  vndLabor: number | null;
  outOfProvince: boolean;
  details: WorkItemDetail[];
};

type WorkloadSummary = {
  codAmount: number;
  tipAmount: number;
  installLabor: number;
  warrantyLabor: number;
  vndLabor: number;
};

type WorkloadData = {
  title?: string;
  workerName?: string;
  saleName?: string;
  date?: string;
  updatedAt?: string;
  updatedBy?: string;
  note?: string;
  rows: WorkloadRow[];
  summary?: WorkloadSummary;
};

const toNumberOrNull = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? null : numberValue;
};

const normalizeDetails = (rawDetails: unknown): WorkItemDetail[] => {
  if (!Array.isArray(rawDetails)) {
    return [];
  }
  return rawDetails.map(detail => {
    const detailRecord = detail as Record<string, unknown>;
    return {
      itemName: String(
        detailRecord.itemName ??
          detailRecord.workItemName ??
          detailRecord.name ??
          detailRecord.title ??
          detailRecord.description ??
          '',
      ),
      itemCount: toNumberOrNull(
        detailRecord.itemCount ?? detailRecord.workCount ?? detailRecord.count ?? detailRecord.coefficient,
      ),
      itemType: String(detailRecord.itemType ?? detailRecord.workType ?? detailRecord.type ?? detailRecord.unit ?? ''),
    };
  });
};

const normalizeWorkload = (payload: unknown): WorkloadData | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const record = payload as Record<string, unknown>;
  const orders = record.orders as unknown[] | undefined;

  if (Array.isArray(orders)) {
    const rows = orders.flatMap(orderItem => {
      const order = orderItem as Record<string, unknown>;
      const items = (order.items as unknown[]) || [];
      if (!Array.isArray(items) || items.length === 0) {
        return [
          {
            orderId: (order.id ?? order.code ?? '') as WorkloadRow['orderId'],
            orderLink: order.link ? String(order.link) : undefined,
            saleName: order.saleName ? String(order.saleName) : '',
            productCode: '',
            quantity: null,
            codAmount: toNumberOrNull(order.codAmount),
            tipAmount: toNumberOrNull(order.tip),
            installLabor: toNumberOrNull(order.totalInstall),
            warrantyLabor: toNumberOrNull(order.totalWarranty),
            vndLabor: toNumberOrNull(order.totalcostVND),
            outOfProvince: Boolean(order.intercity),
            details: normalizeDetails(order.works),
          },
        ];
      }
      return items.map(item => {
        const itemRecord = item as Record<string, unknown>;
        const works = itemRecord.works;
        return {
          orderId: (order.id ?? order.code ?? '') as WorkloadRow['orderId'],
          orderLink: order.link ? String(order.link) : undefined,
          saleName: order.saleName ? String(order.saleName) : '',
          productCode: String(itemRecord.sku ?? ''),
          quantity: toNumberOrNull(itemRecord.quantity),
          codAmount: toNumberOrNull(order.codAmount),
          tipAmount: toNumberOrNull(order.tip),
          installLabor: sumWorkField(works, 'totalInstall'),
          warrantyLabor: sumWorkField(works, 'totalWarranty'),
          vndLabor: sumWorkField(works, 'totalcostVND'),
          outOfProvince: Boolean(order.intercity),
          details: normalizeDetails(works),
        };
      });
    });

    const summary: WorkloadSummary | undefined = {
      codAmount: Number(record.totalCOD ?? 0),
      tipAmount: Number(record.totalRefund ?? 0),
      installLabor: Number(record.totalInstall ?? 0),
      warrantyLabor: Number(record.totalWarranty ?? 0),
      vndLabor: Number(record.totalcostVND ?? 0),
    };

    return {
      title: record.title ? String(record.title) : undefined,
      workerName: record.workerName ? String(record.workerName) : undefined,
      saleName: record.saleName ? String(record.saleName) : undefined,
      date: record.day ? String(record.day) : undefined,
      updatedAt: record.updatedAt ? String(record.updatedAt) : undefined,
      updatedBy: record.updatedBy ? String(record.updatedBy) : undefined,
      note: record.note ? String(record.note) : undefined,
      rows,
      summary,
    };
  }

  const rawRows = (record.rows as unknown[]) || [];
  const rows: WorkloadRow[] = rawRows.map(rawRow => {
    const row = rawRow as Record<string, unknown>;
    return {
      orderId: (row.orderId ?? row.id ?? row.order_id ?? '') as WorkloadRow['orderId'],
      orderLink: row.orderLink ? String(row.orderLink) : undefined,
      saleName: String(row.saleName ?? row.sale ?? ''),
      productCode: String(row.productCode ?? row.product_code ?? row.product ?? ''),
      quantity: toNumberOrNull(row.quantity ?? row.qty),
      codAmount: toNumberOrNull(row.codAmount ?? row.cod ?? row.cod_amount),
      tipAmount: toNumberOrNull(row.tipAmount ?? row.tip ?? row.tip_amount),
      installLabor: toNumberOrNull(row.installLabor ?? row.congLap ?? row.install_labor),
      warrantyLabor: toNumberOrNull(row.warrantyLabor ?? row.congBH ?? row.warranty_labor),
      vndLabor: toNumberOrNull(row.vndLabor ?? row.congVND ?? row.vnd_labor),
      outOfProvince: Boolean(row.outOfProvince ?? row.isOutOfProvince ?? row.out_of_province),
      details: normalizeDetails(row.details ?? row.workItems ?? row.items),
    };
  });

  const rawSummary = (record.summary ?? {}) as Record<string, unknown>;
  const summary = Object.keys(rawSummary).length
    ? {
        codAmount: Number(rawSummary.codAmount ?? rawSummary.cod ?? 0),
        tipAmount: Number(rawSummary.tipAmount ?? rawSummary.tip ?? 0),
        installLabor: Number(rawSummary.installLabor ?? rawSummary.congLap ?? 0),
        warrantyLabor: Number(rawSummary.warrantyLabor ?? rawSummary.congBH ?? 0),
        vndLabor: Number(rawSummary.vndLabor ?? rawSummary.congVND ?? 0),
      }
    : undefined;

  return {
    title: record.title ? String(record.title) : undefined,
    workerName: record.workerName ? String(record.workerName) : undefined,
    saleName: record.saleName ? String(record.saleName) : undefined,
    date: record.date ? String(record.date) : undefined,
    updatedAt: record.updatedAt ? String(record.updatedAt) : undefined,
    updatedBy: record.updatedBy ? String(record.updatedBy) : undefined,
    note: record.note ? String(record.note) : undefined,
    rows,
    summary,
  };
};

const formatMoney = (value: number | string | null | undefined) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) {
    return '-';
  }
  return new Intl.NumberFormat('vi-VN').format(numberValue);
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '-';
  }
  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return '-';
  }
  return parsed.format('HH:mm:ss - DD/MM/YYYY');
};

const sumWorkField = (works: unknown, field: string) => {
  if (!Array.isArray(works) || works.length === 0) {
    return null;
  }
  return works.reduce((total, work) => {
    const workRecord = work as Record<string, unknown>;
    const value = Number(workRecord[field] ?? 0);
    return Number.isNaN(value) ? total : total + value;
  }, 0);
};

const formatWorkloadDate = (value?: string | null) => {
  if (!value) {
    return '';
  }
  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return '';
  }
  const dayOfWeek = parsed.day();
  const dayLabel = dayOfWeek === 0 ? 'Chủ Nhật' : `Thứ ${dayOfWeek + 1}`;
  return `${dayLabel}, ${parsed.format('DD/MM/YYYY')}`;
};

export default function WorkloadPage() {
  const { hash } = useParams<{ hash: string }>();
  const [loading, setLoading] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [workload, setWorkload] = useState<WorkloadData | null>(null);

  const rows = workload?.rows || [];
  const orderRowSpan = useMemo(() => {
    const lookup = new Map<string, { rowSpan: number; firstIndex: number }>();
    rows.forEach((row, index) => {
      const key = String(row.orderId);
      const entry = lookup.get(key);
      if (entry) {
        entry.rowSpan += 1;
      } else {
        lookup.set(key, { rowSpan: 1, firstIndex: index });
      }
    });
    return lookup;
  }, [rows]);

  const summary = useMemo<WorkloadSummary>(() => {
    if (workload?.summary) {
      return workload.summary;
    }
    return rows.reduce(
      (acc, row) => ({
        codAmount: acc.codAmount + Number(row.codAmount || 0),
        tipAmount: acc.tipAmount + Number(row.tipAmount || 0),
        installLabor: acc.installLabor + Number(row.installLabor || 0),
        warrantyLabor: acc.warrantyLabor + Number(row.warrantyLabor || 0),
        vndLabor: acc.vndLabor + Number(row.vndLabor || 0),
      }),
      {
        codAmount: 0,
        tipAmount: 0,
        installLabor: 0,
        warrantyLabor: 0,
        vndLabor: 0,
      },
    );
  }, [rows, workload]);

  useEffect(() => {
    const fetchWorkload = async () => {
      if (!hash) {
        setInvalid(true);
        return;
      }
      try {
        setLoading(true);
        const response = await API.get(`/api/public/statistics/worker-workload/${hash}`);
        if (!response || response.status !== 200) {
          setInvalid(true);
          return;
        }
        const payload = response.data;
        const data = normalizeWorkload(payload?.data || payload);
        console.log(data);
        if (!data || data.rows.length === 0) {
          setInvalid(true);
          return;
        }
        setWorkload(data);
      } catch (error) {
        setInvalid(true);
      } finally {
        setLoading(false);
      }
    };
    fetchWorkload();
  }, [hash]);

  const columns = [
    {
      title: 'Mã đơn hàng',
      dataIndex: 'orderId',
      width: 90,
      render: (_value: WorkloadRow['orderId'], record: WorkloadRow) => {
        const label = `#${record.orderId}`;
        if (!record.orderLink) {
          return <span className="text-blue-600 font-semibold">{label}</span>;
        }
        return (
          <a href={record.orderLink} target="_blank" rel="noreferrer" className="text-blue-600 font-semibold">
            {label}
          </a>
        );
      },
    },
    {
      title: 'Sale phụ trách',
      dataIndex: 'saleName',
      width: 130,
      render: (value: WorkloadRow['saleName']) => value || '-',
    },
    {
      title: 'Mã sản phẩm',
      dataIndex: 'productCode',
      width: 120,
      render: (value: WorkloadRow['productCode']) => value || '-',
    },
    {
      title: 'Thu hộ COD (đ)',
      dataIndex: 'codAmount',
      width: 130,
      align: 'right' as const,
      render: (value: WorkloadRow['codAmount'], record: WorkloadRow, index: number) => {
        const meta = orderRowSpan.get(String(record.orderId));
        const isFirst = meta?.firstIndex === index;
        return {
          children: isFirst ? formatMoney(value) : null,
          props: { rowSpan: isFirst ? meta?.rowSpan ?? 1 : 0 },
        };
      },
    },
    {
      title: 'Hoàn tip (đ)',
      dataIndex: 'tipAmount',
      width: 110,
      align: 'right' as const,
      render: (value: WorkloadRow['tipAmount'], record: WorkloadRow, index: number) => {
        const meta = orderRowSpan.get(String(record.orderId));
        const isFirst = meta?.firstIndex === index;
        return {
          children: isFirst ? formatMoney(value) : null,
          props: { rowSpan: isFirst ? meta?.rowSpan ?? 1 : 0 },
        };
      },
    },
    {
      title: 'Công lắp',
      dataIndex: 'installLabor',
      width: 90,
      align: 'center' as const,
      render: (value: WorkloadRow['installLabor']) => value ?? '-',
    },
    {
      title: 'Công BH',
      dataIndex: 'warrantyLabor',
      width: 90,
      align: 'center' as const,
      render: (value: WorkloadRow['warrantyLabor']) => value ?? '-',
    },
    {
      title: 'Công VNĐ',
      dataIndex: 'vndLabor',
      width: 100,
      align: 'center' as const,
      render: (value: WorkloadRow['vndLabor']) => value ?? '-',
    },
    {
      title: 'Đi tỉnh',
      dataIndex: 'outOfProvince',
      width: 80,
      align: 'center' as const,
      render: (value: WorkloadRow['outOfProvince']) => (value ? <CheckOutlined className="text-green-600" /> : null),
    },
    {
      title: 'Chi tiết thi công',
      dataIndex: 'details',
      render: (_: unknown, record: WorkloadRow) => {
        const detailItems = record.details || [];
        return (
          <div className="space-y-1">
            <div className="font-medium">{(record.quantity ?? '-') + ' x ' + (record.productCode || '-')}</div>
            {detailItems.map((detail, index) => (
              <div key={`${detail.itemName || 'detail'}-${index}`} className="pl-4 text-gray-600">
                └─ {detail.itemName || '-'} - {detail.itemCount ?? '-'} - {detail.itemType || '-'}
              </div>
            ))}
          </div>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Spin />
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Result
          status="404"
          title="Link không hợp lệ hoặc đã hết hạn"
          subTitle="Vui lòng liên hệ Sale để lấy link mới."
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-6">
      <div className="mx-auto w-full max-w-6xl px-4">
        <div className="rounded border border-gray-200 bg-white px-6 py-4 shadow-sm">
          <Row gutter={[16, 8]} align="middle">
            <Col span={24}>
              <Typography.Title level={4} className="!mb-1">
                {workload?.title || 'Dashboard Chốt Ca'}
              </Typography.Title>
              <Typography.Text type="secondary">{formatWorkloadDate(workload?.date)}</Typography.Text>
            </Col>
          </Row>
          <Row gutter={[16, 8]} className="mt-3 text-sm text-gray-600">
            <Col span={24}>
              Thời gian cập nhật dữ liệu: {formatDateTime(workload?.updatedAt)}
              {workload?.updatedBy ? ` bởi ${workload.updatedBy}` : ''}
            </Col>
          </Row>
          <Row gutter={[16, 8]} className="mt-2 text-sm text-gray-600">
            <Col span={24}>
              <strong>{workload?.workerName ? `${workload.workerName}` : ''}</strong>
              {workload?.saleName ? ` - ${workload.saleName}` : ''}
            </Col>
          </Row>
        </div>

        <div className="mt-4 rounded border border-gray-200 bg-white p-4 shadow-sm">
          <Table<WorkloadRow>
            columns={columns}
            dataSource={rows}
            rowKey={(record, index) => `${record.orderId || 'order'}-${record.productCode || 'product'}-${index}`}
            pagination={false}
            scroll={{ x: 'max-content' }}
            summary={() => (
              <Table.Summary fixed="bottom">
                <Table.Summary.Row className="bg-gray-100 font-semibold">
                  <Table.Summary.Cell index={0}>Tổng</Table.Summary.Cell>
                  <Table.Summary.Cell index={1} />
                  <Table.Summary.Cell index={2} />
                  <Table.Summary.Cell index={3} align="right">
                    {formatMoney(summary.codAmount)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    {formatMoney(summary.tipAmount)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="center">
                    {summary.installLabor ?? '-'}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="center">
                    {summary.warrantyLabor ?? '-'}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="center">
                    {summary.vndLabor ?? '-'}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8} />
                  <Table.Summary.Cell index={9} />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </div>

        {workload?.note && <div className="mt-3 text-xs italic text-gray-500">{workload.note}</div>}
      </div>
    </div>
  );
}
